import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Bell,
  Calculator,
  CalendarCheck,
  CalendarPlus,
  Check,
  CheckSquare,
  Note,
  PaperPlaneTilt,
  Sparkle,
  UserGear,
  WarningCircle,
  Wrench,
  X,
} from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

export const TOOL_META = {
  create_task: { icon: CheckSquare, label: "New task" },
  schedule_job: { icon: CalendarPlus, label: "Schedule job" },
  reschedule_job: { icon: CalendarCheck, label: "Reschedule job" },
  update_customer: { icon: UserGear, label: "Update customer" },
  add_fixture: { icon: Wrench, label: "Add fixture" },
  add_installer_note: { icon: Note, label: "Crew note" },
  send_reminder: { icon: Bell, label: "Reminder" },
  quote_job: { icon: Calculator, label: "Quote a job" },
  send_quote: { icon: PaperPlaneTilt, label: "Email quote" },
};

export const Bubble = ({ role, text }) => (
  <div
    data-testid={`ai-msg-${role}`}
    className={`max-w-[75ch] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${
      role === "user"
        ? "self-end bg-indigo-500/15 text-indigo-100"
        : "self-start border border-zinc-800 bg-zinc-900 text-zinc-200"
    }`}
  >
    {text}
  </div>
);

export const ProposalCard = ({ item, onConfirm, onCancel }) => {
  const meta = TOOL_META[item.tool] || { icon: Sparkle, label: "Action" };
  const Icon = meta.icon;
  const done = item.status === "done";
  const cancelled = item.status === "cancelled";
  const working = item.status === "working";
  return (
    <div
      data-testid={`ai-proposal-${item.tool}`}
      className={`w-full max-w-[62ch] self-start rounded-2xl border p-4 transition-colors duration-200 ${
        done
          ? "border-emerald-500/40 bg-emerald-500/5"
          : cancelled
            ? "border-zinc-800 bg-zinc-900/60 opacity-60"
            : "border-indigo-500/40 bg-indigo-500/5"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
            done ? "bg-emerald-500/20 text-emerald-300" : "bg-indigo-500/20 text-indigo-300"
          }`}
        >
          <Icon size={16} weight="bold" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            {meta.label}
          </div>
          <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-zinc-100">
            {item.summary}
          </p>

          {item.warning && !done && !cancelled && (
            <p
              data-testid="ai-proposal-warning"
              className="mt-2 flex items-start gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-[11px] leading-snug text-amber-300"
            >
              <WarningCircle size={13} weight="bold" className="mt-0.5 shrink-0" /> {item.warning}
            </p>
          )}

          {done && (
            <p
              data-testid="ai-proposal-result"
              className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-emerald-300"
            >
              <Check size={13} weight="bold" /> {item.result}
            </p>
          )}
          {done && item.link && (
            <a
              href={item.link}
              data-testid="ai-proposal-open-link"
              className="mt-2 inline-block rounded-full bg-emerald-500/20 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-emerald-200 transition-colors duration-150 hover:bg-emerald-500/30"
            >
              Open quote
            </a>
          )}
          {cancelled && <p className="mt-2 text-xs font-semibold text-zinc-500">Cancelled</p>}

          {item.status === "pending" && (
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                data-testid={`ai-confirm-${item.id}`}
                onClick={() => onConfirm(item)}
                className="flex items-center gap-1.5 rounded-full bg-indigo-500 px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
              >
                <Check size={13} weight="bold" /> Confirm
              </button>
              <button
                type="button"
                data-testid={`ai-cancel-${item.id}`}
                onClick={() => onCancel(item)}
                className="flex items-center gap-1.5 rounded-full border border-zinc-700 px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-zinc-500 hover:text-zinc-200"
              >
                <X size={13} weight="bold" /> Cancel
              </button>
            </div>
          )}
          {working && (
            <p className="mt-3 flex items-center gap-1.5 text-xs text-indigo-300">
              <Sparkle size={13} weight="bold" className="animate-pulse" /> Working on it…
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Shared assistant chat state used by both the full page and the floating dock.
 * onActed() fires after any successful execute/undo so callers can refresh panels.
 */
export function useAssistant({ onActed, context } = {}) {
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const uid = useRef(0);

  const undoById = useCallback(
    async (id) => {
      try {
        const { data } = await api.post(`/ai/actions/${id}/undo`);
        toast.success(data.message || "Undone");
        onActed?.();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not undo");
      }
    },
    [onActed],
  );

  const setProp = useCallback(
    (id, patch) =>
      setMessages((m) =>
        m.map((x) => (x.kind === "proposal" && x.id === id ? { ...x, ...patch } : x)),
      ),
    [],
  );

  const send = useCallback(
    async (text) => {
      const question = (text || "").trim();
      if (!question || busy) return;
      setMessages((m) => [...m, { key: `u${uid.current++}`, role: "user", text: question }]);
      setBusy(true);
      try {
        const ctx = context
          ? {
              kind: context.kind,
              id: context.id,
              label: context.label,
              quote_id: context.quote_id,
              quote_code: context.quote_code,
            }
          : null;
        const { data } = await api.post("/ai/agent", {
          session_id: sessionId,
          message: question,
          context: ctx,
        });
        if (data.session_id && !sessionId) setSessionId(data.session_id);
        const additions = [];
        if (data.reply)
          additions.push({ key: `a${uid.current++}`, role: "assistant", text: data.reply });
        for (const p of data.proposals || []) {
          if (p.error) {
            additions.push({ key: `e${uid.current++}`, role: "assistant", text: p.error });
          } else {
            additions.push({
              key: `p${uid.current++}`,
              kind: "proposal",
              id: p.id,
              tool: p.tool,
              args: p.args,
              summary: p.summary,
              warning: p.warning,
              status: "pending",
            });
          }
        }
        setMessages((m) => [...m, ...additions]);
      } catch (err) {
        toast.error(
          formatApiErrorDetail(err.response?.data?.detail) || "The assistant could not answer",
        );
      }
      setBusy(false);
    },
    [busy, sessionId, context],
  );

  const confirmProp = useCallback(
    async (item) => {
      setProp(item.id, { status: "working" });
      try {
        const { data } = await api.post("/ai/agent/execute", {
          session_id: sessionId,
          tool: item.tool,
          args: item.args,
        });
        setProp(item.id, { status: "done", result: data.message || "Done", link: data.link || "" });
        if (data.undoable && data.action_id) {
          toast.success(data.message || "Done", {
            duration: 8000,
            action: { label: "Undo", onClick: () => undoById(data.action_id) },
          });
        } else {
          toast.success(data.message || "Done");
        }
        onActed?.();
      } catch (err) {
        setProp(item.id, { status: "pending" });
        toast.error(
          formatApiErrorDetail(err.response?.data?.detail) || "Could not complete the action",
        );
      }
    },
    [sessionId, setProp, undoById, onActed],
  );

  const cancelProp = useCallback((item) => setProp(item.id, { status: "cancelled" }), [setProp]);

  const sendSketch = useCallback(
    async (file, text) => {
      if (!file || busy) return;
      const note = (text || "").trim();
      setMessages((m) => [
        ...m,
        { key: `u${uid.current++}`, role: "user", text: `[Sketch photo: ${file.name}] ${note}` },
      ]);
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("message", note);
        fd.append("session_id", sessionId);
        const { data } = await api.post("/ai/agent/sketch", fd);
        if (data.session_id && !sessionId) setSessionId(data.session_id);
        const additions = [];
        if (data.sketch?.text)
          additions.push({
            key: `s${uid.current++}`,
            role: "assistant",
            text: `Read from the sketch:\n${data.sketch.text}\n≈ ${data.sketch.sqft} SF · ${data.sketch.edge_lf} LF finished edge`,
          });
        if (data.reply)
          additions.push({ key: `a${uid.current++}`, role: "assistant", text: data.reply });
        for (const p of data.proposals || []) {
          additions.push(
            p.error
              ? { key: `e${uid.current++}`, role: "assistant", text: p.error }
              : { key: `p${uid.current++}`, kind: "proposal", id: p.id, tool: p.tool, args: p.args,
                  summary: p.summary, warning: p.warning, status: "pending" },
          );
        }
        setMessages((m) => [...m, ...additions]);
      } catch (err) {
        toast.error(
          formatApiErrorDetail(err.response?.data?.detail) || "Could not read that sketch",
        );
      }
      setBusy(false);
    },
    [busy, sessionId],
  );

  const reset = useCallback(() => {
    setSessionId("");
    setMessages([]);
  }, []);

  return {
    messages,
    setMessages,
    busy,
    sessionId,
    setSessionId,
    uid,
    send,
    sendSketch,
    confirmProp,
    cancelProp,
    reset,
  };
}
